import { DateTimePart, DateTimeService, DurationService } from '@mews/framework';
import { getToken } from '@optimus-web/common';
import { DENSE_B2B_LIGHT_THEME } from '@optimus-web/theme';
import { TimeUnit } from '@mews/enums';
import { LINE_HEIGHT, CORNER_RADIUS, ICON, DATE_LEGEND_HEIGHT } from '../../constants';
import { drawIcon, drawUseRectangle } from '../../utils/drawUtils';
import { round } from '../../utils/mathUtils';
import { TimelineModel, TimelineState } from '../../types';
import { TimelineService } from '../../timelineService';

const addSeconds = DateTimeService.add(DateTimePart.Seconds);

const isBetween = (value: number, coordinate1: number, coordinate2: number) =>
    coordinate1 <= value && value <= coordinate2;

export default function ResourceUsePlaceholder() {
    return {
        render(state: TimelineState, model: TimelineModel) {
            drawUsePlaceholder(state, model);
        },
    };

    function drawUsePlaceholder(
        {
            ctx,
            xAxis,
            yAxis,
            scale,
            timezone,
            timeUnit,
            mouse,
            startOffset: startOffsetString,
            endOffset: endOffsetString,
            maximumAvailableEditableHistoryIntervalInMilliseconds,
        }: TimelineState,
        model: TimelineModel
    ) {
        if (!mouse) {
            return;
        }

        const topSpacing = 3.5;
        const bottomSpacing = 2.5;
        const x = xAxis.invert(mouse.x);
        const y = yAxis.invert(mouse.y);

        const thisTimeUnitIndex = round(x);

        const minTimeUnitIndex = TimelineService.utcToTimeUnitIndex(
            addSeconds(
                TimelineService.indexToTimeUnitDateTime(0, timeUnit),
                -(maximumAvailableEditableHistoryIntervalInMilliseconds / 1000)
            ).value,
            timezone,
            timeUnit
        );

        const resourceIndex = round(y);
        const resource = model.getResourceAtY(mouse);
        const isOverbookingRow = resource?.floorNumber === undefined || resource?.number === undefined;

        if (thisTimeUnitIndex <= minTimeUnitIndex || isOverbookingRow || !resource.resourceCategoryId) {
            // do not create reservations before editable history window or in overbooking row
            model.setResourceUsePlaceholder(null);
            return;
        }

        const lineHeight = LINE_HEIGHT * scale;

        const p = model.getResourceUsePlaceholder();
        if (p && isBetween(mouse.x, p.x1, p.x2) && isBetween(mouse.y, p.y1, p.y2)) {
            // mouse is inside set placeholder
            const color = getToken('borderInteractiveBoldActive')({ theme: DENSE_B2B_LIGHT_THEME });
            drawUseRectangle({
                ctx,
                x1: p.x1,
                x2: p.x2,
                y1: p.y1,
                y2: p.y2,
                radius: CORNER_RADIUS / scale,
                bgColor: getToken('backgroundStaticFlat')({ theme: DENSE_B2B_LIGHT_THEME }),
                strokeColor: color,
                dashed: true,
            });
            const iconX = p.x1 + (p.x2 - p.x1) / 2 - 8 * scale;
            const iconY = p.y1 + (lineHeight / 2 - 8 * scale - topSpacing * scale);
            drawIcon(ctx, iconX, iconY, ICON.ADD, scale, color);
        } else if (startOffsetString && endOffsetString && model.getResourceAtY(mouse)) {
            // mouse is somewhere where new placeholder could be drawn
            const confirmedPlaceholder = model.getConfirmedResourceUsePlaceholder();
            const thisTimeUnitUtc = TimelineService.indexToTimeUnitDateTime(thisTimeUnitIndex, timeUnit).value;

            const options = {
                startOffset: startOffsetString,
                endOffset: endOffsetString,
                timeUnit,
                timezone,
            };

            const coordinates = getXCoordinatesWithOffsets(
                thisTimeUnitUtc,
                TimelineService.indexToTimeUnitDateTime(thisTimeUnitIndex + 1, timeUnit).value,
                options
            );

            if (confirmedPlaceholder) {
                const r = model.findResourceById(confirmedPlaceholder.resourceId);
                const minX = TimelineService.utcToTimeUnitIndex(confirmedPlaceholder.startUtc, timezone, timeUnit);
                const maxX = TimelineService.utcToTimeUnitIndex(confirmedPlaceholder.endUtc, timezone, timeUnit);

                if (
                    r &&
                    isBetween(mouse.x, xAxis(minX) as number, xAxis(maxX) as number) &&
                    isBetween(mouse.y - DATE_LEGEND_HEIGHT, yAxis(r.minY) as number, yAxis(r.maxY) as number)
                ) {
                    // mouse is inside confirmed placeholder
                    return;
                }
            }

            // coordinates of placeholder (-1px in length to exclude the borders)
            const x1 = (xAxis(coordinates.x1) as number) + 1;
            const x2 = (xAxis(coordinates.x2) as number) - 1;
            const y1 = (yAxis(resourceIndex) as number) + topSpacing * scale;
            const y2 = (yAxis(resourceIndex) as number) + lineHeight - bottomSpacing * scale;

            const lineCenter = y2 - lineHeight / 2;

            if (isBetween(mouse.x, x1, x2) && isBetween(mouse.y, y1, y2)) {
                // mouse is in placeholder
                const previousUse = model.getResourceUseAtPosition({ x: x1, y: lineCenter });
                const nextUse = model.getResourceUseAtPosition({ x: x2, y: lineCenter });
                if (!previousUse && !nextUse) {
                    return model.setResourceUsePlaceholder({
                        x1,
                        x2,
                        y1,
                        y2,
                        startUtc: coordinates.offsetStartDateTime.value,
                        endUtc: coordinates.offsetEndDateTime.value,
                    });
                }
            }

            if (thisTimeUnitIndex > minTimeUnitIndex) {
                const coordinates2 = getXCoordinatesWithOffsets(
                    TimelineService.indexToTimeUnitDateTime(thisTimeUnitIndex - 1, timeUnit).value,
                    thisTimeUnitUtc,
                    options
                );

                // coordinates of previous placeholder
                const x3 = xAxis(coordinates2.x1) as number;
                const x4 = xAxis(coordinates2.x2) as number;

                if (isBetween(mouse.x, x3, x4) && isBetween(mouse.y, y1, y2)) {
                    // mouse is in previous placeholder
                    const previousUse = model.getResourceUseAtPosition({ x: x3, y: lineCenter });
                    const nextUse = model.getResourceUseAtPosition({ x: x4, y: lineCenter });
                    if (!previousUse && !nextUse) {
                        return model.setResourceUsePlaceholder({
                            x1: x3,
                            x2: x4,
                            y1,
                            y2,
                            startUtc: coordinates2.offsetStartDateTime.value,
                            endUtc: coordinates2.offsetEndDateTime.value,
                        });
                    }
                }
            }

            model.setResourceUsePlaceholder(null);
        }
    }
}

export function getXCoordinatesWithOffsets<
    O extends {
        startOffset: string;
        endOffset: string;
        timezone: string;
        timeUnit: TimeUnit;
    },
>(startUtc: string, endUtc: string, { startOffset, endOffset, timezone, timeUnit }: O) {
    const startDateTime = DateTimeService.fromUtcString(startUtc, timezone);
    const endDateTime = DateTimeService.fromUtcString(endUtc, timezone);

    const offsetStartDateTime = DateTimeService.addSeconds(
        startDateTime,
        DurationService.asSeconds(DurationService.parse(startOffset))
    );
    const offsetStartUtc = DateTimeService.toUtc(offsetStartDateTime);

    const offsetEndDateTime = DateTimeService.addSeconds(
        endDateTime,
        DurationService.asSeconds(DurationService.parse(endOffset))
    );
    const offsetEndUtc = DateTimeService.toUtc(offsetEndDateTime);

    const x1 = TimelineService.utcToTimeUnitIndex(offsetStartUtc.value, timezone, timeUnit);
    const x2 = TimelineService.utcToTimeUnitIndex(offsetEndUtc.value, timezone, timeUnit);

    return {
        x1,
        x2,
        offsetStartDateTime: offsetStartUtc,
        offsetEndDateTime: offsetEndUtc,
        startDateTime,
        endDateTime,
    };
}
